/**
 * Registro canônico dos tipos de notificação.
 *
 * Antes deste arquivo a lista existia em três lugares que ninguém comparava:
 * o CHECK `notifications_tipo_check` no banco, os literais espalhados pelas Edge
 * Functions e o mapa de rótulos da tela. As três divergiram, e sete tipos
 * gravados pelo código eram recusados pelo banco — em silêncio, porque o
 * PostgREST devolve o erro em `{ error }` em vez de lançar. Nenhuma dessas
 * notificações jamais chegou a um usuário.
 *
 * Agora este arquivo é a fonte única. A migration escreve o CHECK a partir
 * daqui, as telas leem o rótulo daqui, e
 * `tests/unit/lib/notification-types.test.ts` falha se um `tipo:` gravado no
 * código não estiver listado aqui ou se o CHECK divergir.
 *
 * Para acrescentar um tipo: registre aqui, escreva a migration ampliando o
 * CHECK com a mesma lista, e o teste confirma que as duas pontas batem.
 */

export type NotificationTypeMeta = {
  /** O que o usuário lê no crachá. Nunca o slug — slug é nome técnico. */
  label: string;
  /** Para onde o clique leva, ou null quando não há tela correspondente. */
  route: string | null;
};

export const NOTIFICATION_TYPES = {
  vencimento: { label: "Vencimento", route: "/cobrancas" },
  inadimplencia: { label: "Inadimplência", route: "/cobrancas" },
  contrato_expirando: { label: "Contrato Expirando", route: "/contratos" },
  reajuste: { label: "Reajuste", route: "/contratos" },
  sistema: { label: "Sistema", route: null },
  pagamento: { label: "Pagamento", route: "/cobrancas" },

  ocr_concluido: { label: "Conta Processada", route: "/conectores" },
  ocr_falhou: { label: "Falha na Leitura", route: "/conectores" },
  gmail_sync: { label: "Sincronização Gmail", route: "/conectores" },

  alerta_risco_estrutural: { label: "Alerta de Risco", route: "/alertas" },
  referral_convertido: { label: "Indicação", route: "/indicar" },
  referral_cadastro: { label: "Indicação", route: "/indicar" },
  // Vai para superadmins, não para o cliente — mas cai na mesma tela e no mesmo
  // sino, então precisa de rótulo como qualquer outro.
  novo_parceiro_pendente: { label: "Novo Parceiro", route: "/superadmin/partners" },

  anunciar_contrato: { label: "Anúncio", route: "/anunciar" },
  anunciar_novo_interesse: { label: "Novo Interesse", route: "/anunciar" },
  anunciar_visita_agendada: { label: "Visita Agendada", route: "/anunciar" },
  anunciar_proposta_recebida: { label: "Proposta Recebida", route: "/anunciar" },
  anunciar_contrato_gerado: { label: "Contrato Gerado", route: "/anunciar" },
  // GIFI Vitrine — classificados. Rota é a caixa do anunciante, onde a ação acontece.
  vitrine_anuncio_aprovado:   { label: "Anúncio Aprovado",    route: "/vitrine/meus-anuncios" },
  vitrine_anuncio_rejeitado:  { label: "Anúncio Rejeitado",   route: "/vitrine/meus-anuncios" },
  vitrine_anuncio_expirando:  { label: "Anúncio Expirando",   route: "/vitrine/meus-anuncios" },
  vitrine_novo_lead:          { label: "Novo Contato",        route: "/vitrine/leads" },
  vitrine_creditos_expirando: { label: "Créditos Expirando",  route: "/vitrine/creditos" },
  // A decisão sobre a CANDIDATURA de anunciante, que é outra coisa da decisão
  // sobre um anúncio. A tela "Cadastro em análise" promete este aviso desde que
  // existe — e até 08/09/2026 nada o enviava.
  anunciante_aprovado:        { label: "Anunciante Aprovado",  route: "/vitrine/meus-anuncios" },
  anunciante_rejeitado:       { label: "Cadastro Recusado",    route: "/vitrine" },

  // O plano Free tem período gratuito por 3 meses. Este é o aviso ATIVO do fim
  // dele; o passivo é o `PeriodoGratisAviso` no cartão do plano. Nenhum dos
  // dois promete cobrança.
  periodo_gratis_terminando: { label: "Período Gratuito", route: "/assinatura" },
  // Decidido em 02/10/2026: ao contrário do aviso acima, este É para quem já
  // passou do prazo — porque agora existe pagamento de verdade (R$ 9,90) a
  // fazer, e a conta está travada em `TravaPeriodoGratis` até que aconteça.
  periodo_gratis_travado: { label: "Período Gratuito Encerrado", route: "/assinatura" },

  // IBS/CBS — o enquadramento da pessoa física (migration 20261006000004). Três
  // degraus, e só o que SOBE avisa: perto do limite → passou do limite → enquadrado.
  // O texto sempre manda confirmar com o contador: é aviso, não parecer.
  ibscbs_limite_proximo: { label: "IBS/CBS — Perto do Limite", route: "/fiscal/ibs-cbs" },
  ibscbs_enquadramento_ano_seguinte: { label: "IBS/CBS — Ano Seguinte", route: "/fiscal/ibs-cbs" },
  ibscbs_enquadrado: { label: "IBS/CBS — Enquadramento", route: "/fiscal/ibs-cbs" },
} as const satisfies Record<string, NotificationTypeMeta>;

export type NotificationType = keyof typeof NOTIFICATION_TYPES;

/** Ordenado, porque a migration e o teste comparam listas. */
export const NOTIFICATION_TYPE_SLUGS: readonly string[] =
  Object.keys(NOTIFICATION_TYPES).sort();

/**
 * Rótulo legível. Um tipo desconhecido nunca deve chegar aqui — o teste impede
 * que o código grave um —, mas se chegar, "Notificação" é melhor que expor o
 * slug técnico para o usuário.
 */
export function notificationLabel(tipo: string): string {
  return (NOTIFICATION_TYPES as Record<string, NotificationTypeMeta>)[tipo]?.label ?? "Notificação";
}

/** Rota de destino do clique, ou null quando não há para onde ir. */
export function notificationRoute(tipo: string): string | null {
  return (NOTIFICATION_TYPES as Record<string, NotificationTypeMeta>)[tipo]?.route ?? null;
}

/**
 * O texto do botão que leva ao destino.
 *
 * A tela decidia isso com um ternário sobre dois tipos: `contrato_expirando` e
 * `reajuste` diziam "Ver contrato", e **todo o resto** dizia "Ver cobrança" —
 * inclusive a conta lida pelo OCR, que vai para Conectores, e a indicação
 * convertida, que vai para Indicar. O botão prometia uma tela e abria outra.
 *
 * O verbo sai da rota, que já é a fonte única do destino: assim um tipo novo
 * nasce com o texto certo sem ninguém lembrar de mexer aqui.
 */
const CTA_POR_ROTA: Record<string, string> = {
  "/cobrancas": "Ver cobrança",
  "/contratos": "Ver contrato",
  "/conectores": "Ver conta",
  "/alertas": "Ver alerta",
  "/indicar": "Ver indicação",
  "/anunciar": "Ver anúncio",
  "/superadmin/partners": "Ver parceiro",
};

export function notificationCta(tipo: string): string {
  const rota = notificationRoute(tipo);
  if (!rota) return "Abrir";
  return CTA_POR_ROTA[rota] ?? "Abrir";
}
